/**
 * Light/dark theme selection.
 *
 * A *device* preference, not an account one — deliberately unlike the
 * language (`users.locale`, which follows you across devices). Which theme
 * reads better depends on the screen and the room you're in, so the same
 * person legitimately wants dark on a phone at night and light on a desktop
 * by a window. Keeping it in `localStorage` also means it applies before the
 * first render, with no session round-trip and no flash of the wrong theme.
 *
 * Light is the default. A stored choice always wins, so anyone who has
 * already picked dark keeps it — the default only decides what someone who
 * never opens settings sees.
 */
export const THEMES = ['dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'bolao.theme';

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

export function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    // Private-mode Safari and similar can throw on access; fall through.
  }
  return 'light';
}

/**
 * Writes the theme onto <html> as `data-theme`, which is what the
 * `:root[data-theme='dark']` token block in `styles/tokens.css` keys off.
 * Light is the token file's own default, so it carries no attribute — that
 * way the page paints in the default theme before this ever runs, and a
 * failure to run it leaves the app correctly themed rather than unstyled.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }
  // Keeps the mobile browser chrome (address bar) in step with the page
  // rather than leaving a navy band above a white app.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#ffffff' : '#0f172a');
}

export function setStoredTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Not fatal — the choice just won't survive a reload.
  }
  applyTheme(theme);
}
